/**
 * @component group-settings-moderation
 * @css group-settings-moderation.css
 */
import { escapeHtml } from '../utils/html-utils.js';
import { buildGroupCommentUrl, buildGroupPostUrl } from '../utils/group-permalink.js';

const STATUS_FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' }
];

const CONTENT_ACTIONS = [
  { value: 'warning', label: 'Send warning' },
  { value: 'content_removal', label: 'Remove content' }
];

const MEMBER_ACTIONS = [
  { value: 'warning', label: 'Send warning' },
  { value: 'temp_suspend', label: 'Suspend member' },
  { value: 'remove_member', label: 'Remove member' }
];

const DESTRUCTIVE_ACTIONS = new Set(['content_removal', 'temp_suspend', 'remove_member']);

class GroupSettingsModeration extends HTMLElement {
  constructor() {
    super();
    this.groupId = null;
    this.group = null;
    this.membership = null;
    this.reports = [];
    this.statusFilter = 'open';
    this.loading = true;
    this.error = null;
    this.openCount = 0;
    this.expandedReportId = null;
    this.actionReasons = {};
    this.submittingReportId = null;
  }

  setContext({ groupId, group, membership, currentUser }) {
    this.groupId = groupId;
    this.group = group;
    this.membership = membership;
    this.currentUser = currentUser;
    this.load();
  }

  async load() {
    if (!window.API || !this.groupId) {
      return;
    }
    this.loading = true;
    this.error = null;
    this.render();
    try {
      const data = await window.API.request(
        `/groups/${this.groupId}/reports?status=${encodeURIComponent(this.statusFilter)}`
      );
      this.reports = data.reports || [];
      if (this.statusFilter === 'open') {
        this.openCount = this.reports.length;
        this.dispatchEvent(new CustomEvent('group-reports-count', {
          detail: { openCount: this.openCount },
          bubbles: true
        }));
      }
    } catch (e) {
      this.error = e.message || 'Could not load reports';
      this.reports = [];
    } finally {
      this.loading = false;
      this.render();
      this.attachListeners();
    }
  }

  formatDateTime(iso) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return iso;
    }
  }

  targetTypeLabel(type) {
    if (type === 'post') return 'Post';
    if (type === 'comment') return 'Comment';
    if (type === 'member') return 'Member';
    return type;
  }

  buildPermalink(report) {
    if (!report.permalink) {
      return null;
    }
    const { groupId, postId, commentId } = report.permalink;
    if (commentId) {
      return buildGroupCommentUrl({ groupId, postId, commentId });
    }
    return buildGroupPostUrl({ groupId, postId });
  }

  actionsForReport(report) {
    if (report.target_type === 'member') {
      return MEMBER_ACTIONS;
    }
    return CONTENT_ACTIONS;
  }

  renderFilters() {
    return STATUS_FILTERS.map((f) => {
      const active = f.id === this.statusFilter ? 'active' : '';
      const badge = f.id === 'open' && this.openCount > 0 && this.statusFilter !== 'open'
        ? ` (${this.openCount})`
        : '';
      return `<button type="button" class="filter-btn ${active}" data-filter="${f.id}">${f.label}${badge}</button>`;
    }).join('');
  }

  renderActionPanel(report) {
    if (this.expandedReportId !== report.id) {
      return '';
    }
    const actions = this.actionsForReport(report);
    const options = actions.map((a) => {
      const selected = this.actionReasons[report.id]?.action === a.value ? 'selected' : '';
      return `<option value="${a.value}" ${selected}>${escapeHtml(a.label)}</option>`;
    }).join('');
    const reason = escapeHtml(this.actionReasons[report.id]?.reason || '');
    const disabled = this.submittingReportId === report.id ? 'disabled' : '';

    return `
      <div class="report-action-panel">
        <div class="form-group">
          <label class="form-label" for="action-select-${report.id}">Moderation action</label>
          <select id="action-select-${report.id}" class="form-control action-select" data-report-id="${report.id}" ${disabled}>
            <option value="">Choose an action…</option>
            ${options}
          </select>
        </div>
        <div class="form-group">
          <label class="form-label" for="action-reason-${report.id}">Reason (required)</label>
          <textarea id="action-reason-${report.id}" class="form-control action-reason" data-report-id="${report.id}" rows="3" minlength="3" maxlength="5000" placeholder="Explain the decision for the audit log…" ${disabled}>${reason}</textarea>
        </div>
        <div class="report-action-buttons">
          <button type="button" class="btn btn-primary btn-sm apply-action-btn" data-report-id="${report.id}" ${disabled}>Apply action</button>
          <button type="button" class="btn btn-secondary btn-sm dismiss-report-btn" data-report-id="${report.id}" ${disabled}>Dismiss report</button>
          <button type="button" class="btn btn-outline btn-sm cancel-action-btn" data-report-id="${report.id}" ${disabled}>Cancel</button>
        </div>
      </div>
    `;
  }

  renderReportCard(report) {
    const permalink = this.buildPermalink(report);
    const viewLink = permalink
      ? `<a href="${escapeHtml(permalink)}" class="report-permalink-link" data-route="${escapeHtml(permalink)}">View in group</a>`
      : '';
    const authorLine = report.target_author_username
      ? `<div class="report-meta">By @${escapeHtml(report.target_author_username)}</div>`
      : '';
    const preview = report.target_preview
      ? `<blockquote class="report-preview">${escapeHtml(report.target_preview)}</blockquote>`
      : '<p class="empty-hint">Target content unavailable (may have been removed).</p>';

    return `
      <article class="card report-card" data-report-id="${report.id}">
        <div class="card-body">
          <div class="report-card-header">
            <span class="report-type-badge">${escapeHtml(this.targetTypeLabel(report.target_type))}</span>
            <span class="report-status-badge status-${escapeHtml(report.status)}">${escapeHtml(report.status)}</span>
            <span class="report-time">${escapeHtml(this.formatDateTime(report.created_at))}</span>
          </div>
          <div class="report-meta">Reported by @${escapeHtml(report.reporter_username || 'unknown')}</div>
          <div class="report-reason"><strong>Reason:</strong> ${escapeHtml(report.reason)}</div>
          ${report.details ? `<div class="report-details">${escapeHtml(report.details)}</div>` : ''}
          ${authorLine}
          ${preview}
          ${viewLink}
          <div class="report-card-actions">
            ${report.status === 'open' || report.status === 'in_review'
    ? `<button type="button" class="btn btn-sm btn-secondary take-action-btn" data-report-id="${report.id}">Take action</button>`
    : ''}
          </div>
          ${this.renderActionPanel(report)}
        </div>
      </article>
    `;
  }

  renderContent() {
    if (this.loading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading reports…</div>
        </div>
      `;
    }

    if (this.error) {
      return `<div class="alert alert-error">${escapeHtml(this.error)}</div>`;
    }

    if (this.reports.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">🚩</div>
          <h3>No reports</h3>
          <p>There are no reports with the selected status.</p>
        </div>
      `;
    }

    return `<div class="reports-list">${this.reports.map((r) => this.renderReportCard(r)).join('')}</div>`;
  }

  render() {
    this.innerHTML = `
      <div class="group-settings-moderation-content">
        <section class="settings-group">
          <div class="group-header">
            <h3>Reports queue</h3>
            <p class="group-description">Review member reports and take moderation actions.</p>
          </div>
          <div class="reports-filters">${this.renderFilters()}</div>
          ${this.renderContent()}
        </section>
      </div>
    `;
  }

  attachListeners() {
    this.querySelectorAll('.filter-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.statusFilter = btn.getAttribute('data-filter');
        this.expandedReportId = null;
        this.load();
      });
    });

    this.querySelectorAll('.take-action-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const reportId = parseInt(btn.getAttribute('data-report-id'), 10);
        this.expandedReportId = reportId;
        if (!this.actionReasons[reportId]) {
          this.actionReasons[reportId] = { action: '', reason: '' };
        }
        this.render();
        this.attachListeners();
      });
    });

    this.querySelectorAll('.cancel-action-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.expandedReportId = null;
        this.render();
        this.attachListeners();
      });
    });

    this.querySelectorAll('.action-select').forEach((select) => {
      select.addEventListener('change', () => {
        const reportId = parseInt(select.getAttribute('data-report-id'), 10);
        if (!this.actionReasons[reportId]) {
          this.actionReasons[reportId] = { action: '', reason: '' };
        }
        this.actionReasons[reportId].action = select.value;
      });
    });

    this.querySelectorAll('.action-reason').forEach((textarea) => {
      textarea.addEventListener('input', () => {
        const reportId = parseInt(textarea.getAttribute('data-report-id'), 10);
        if (!this.actionReasons[reportId]) {
          this.actionReasons[reportId] = { action: '', reason: '' };
        }
        this.actionReasons[reportId].reason = textarea.value;
      });
    });

    this.querySelectorAll('.apply-action-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const reportId = parseInt(btn.getAttribute('data-report-id'), 10);
        this.applyAction(reportId);
      });
    });

    this.querySelectorAll('.dismiss-report-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const reportId = parseInt(btn.getAttribute('data-report-id'), 10);
        this.dismissReport(reportId);
      });
    });
  }

  getReport(reportId) {
    return this.reports.find((r) => Number(r.id) === reportId);
  }

  async applyAction(reportId) {
    const report = this.getReport(reportId);
    if (!report) return;

    const form = this.actionReasons[reportId] || {};
    const actionType = form.action || this.querySelector(`#action-select-${reportId}`)?.value;
    const reason = (form.reason || this.querySelector(`#action-reason-${reportId}`)?.value || '').trim();

    if (!actionType) {
      this.showAlert('Choose an action first.', 'error');
      return;
    }
    if (reason.length < 3) {
      this.showAlert('Please enter a reason (at least 3 characters).', 'error');
      return;
    }

    if (DESTRUCTIVE_ACTIONS.has(actionType)) {
      const label = this.actionsForReport(report).find((a) => a.value === actionType)?.label || actionType;
      if (!window.confirm(`Apply "${label}" to this report? This cannot be undone easily.`)) {
        return;
      }
    }

    this.submittingReportId = reportId;
    this.render();
    this.attachListeners();

    try {
      await window.API.request(`/groups/${this.groupId}/reports/${reportId}/actions`, {
        method: 'POST',
        body: JSON.stringify({
          action_type: actionType,
          target_type: report.target_type,
          target_id: report.target_id,
          reason,
          metadata_json: {}
        })
      });
      this.expandedReportId = null;
      delete this.actionReasons[reportId];
      this.showAlert('Moderation action recorded.', 'success');
      await this.load();
      this.dispatchEvent(new CustomEvent('group-members-changed', { bubbles: true }));
    } catch (e) {
      this.showAlert(e.message || 'Could not apply action', 'error');
    } finally {
      this.submittingReportId = null;
      this.render();
      this.attachListeners();
    }
  }

  async dismissReport(reportId) {
    const form = this.actionReasons[reportId] || {};
    const reason = (form.reason || this.querySelector(`#action-reason-${reportId}`)?.value || '').trim();

    if (!window.confirm('Dismiss this report with no moderation action?')) {
      return;
    }

    this.submittingReportId = reportId;
    this.render();
    this.attachListeners();

    try {
      await window.API.request(`/groups/${this.groupId}/reports/${reportId}/dismiss`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {})
      });
      this.expandedReportId = null;
      delete this.actionReasons[reportId];
      this.showAlert('Report dismissed.', 'success');
      await this.load();
    } catch (e) {
      this.showAlert(e.message || 'Could not dismiss report', 'error');
    } finally {
      this.submittingReportId = null;
      this.render();
      this.attachListeners();
    }
  }

  showAlert(message, type = 'info') {
    this.dispatchEvent(new CustomEvent('group-moderation-alert', {
      detail: { message, type },
      bubbles: true
    }));
  }
}

customElements.define('group-settings-moderation', GroupSettingsModeration);
export default GroupSettingsModeration;
